import { useTheme } from "@mui/material";
import {
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
} from "recharts";

const CHART_MODE_CONFIG = {
  simulation: {
    yAxisLabel: "Simulated Return (USD)",
    scaleValue: (value, amount) => value * amount,
    formatAxisValue: (value) =>
      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 2 }).format(value),
    formatTooltipValue: (value) =>
      new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(value),
    tooltipName: "Simulated return",
  },
  percentage: {
    yAxisLabel: "Return (%)",
    scaleValue: value => value,
    formatAxisValue: (value) => `${Number(value).toFixed(3)}%`,
    formatTooltipValue: (value) => `${value.toFixed(3)}%`,
    tooltipName: "Return",
  },
  nav: {
    yAxisLabel: "NAV",
    scaleValue: (value) => value,
    formatAxisValue: (value) => Number(value).toFixed(3),
    formatTooltipValue: (value) => value.toFixed(3),
    tooltipName: "NAV",
  },
};

const COMPARE_COLORS = ["#ff6b6b", "#ffd93d", "#6bcb77", "#4d96ff", "#ff922b"];

/**
 * Chart
 * @param {Array} strategies - Unified strategies array. Index 0 is the main strategy; 1..n are comparisons.
 * @param {string} period - Current selected period, used for x-axis formatting.
 */
const Chart = ({ strategies = [], investmentAmount = null, showPercentage = false, period, isComparison = false }) => {
  const currentTheme = useTheme();

  const mainStrategy = strategies[0];
  const compareStrategiesData = strategies.slice(1);
  const data = mainStrategy?.chartData || [];

  const chartMode =
    investmentAmount !== null && investmentAmount > 0
      ? "simulation"
      : showPercentage
        ? "percentage"
        : "nav";

  const modeConfig = CHART_MODE_CONFIG[chartMode];

  const scaleValue = (point) =>
    chartMode === "percentage"
      ? point.displayPercent
      : modeConfig.scaleValue(point.value, investmentAmount);

  const scaledData = Array.isArray(data)
    ? data.map((point) => ({ ...point, value: scaleValue(point) }))
    : [];

  const mergedData = (() => {
    const dateMap = new Map();
    scaledData.forEach((point) => {
      dateMap.set(point.date, { ...point });
    });
    compareStrategiesData.forEach((compareStrategy) => {
      const key = `compare_${compareStrategy.idStrategy}`;
      compareStrategy.chartData.forEach((point) => {
        const scaledCompareValue = scaleValue(point);
        const existing = dateMap.get(point.date) || { date: point.date, value: null };
        dateMap.set(point.date, { ...existing, [key]: scaledCompareValue });
      });
    });
    return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  })();

  const allValues = [
    ...mergedData.map((point) => point.value),
    ...compareStrategiesData.flatMap((compareStrategy) =>
      mergedData.map((point) => point[`compare_${compareStrategy.idStrategy}`])
    ),
  ].filter((value) => value !== null && value !== undefined && !Number.isNaN(Number(value)));

  const hasData = allValues.length > 0;
  const min = hasData ? Math.min(...allValues) : 0;
  const max = hasData ? Math.max(...allValues) : 1;
  const pad = Math.max((max - min) * 0.1, 0.02);
  const domain = [min - pad, max + pad];

  const yAxisTicks = (() => {
    const domainMin = domain[0];
    const domainMax = domain[1];
    const anchor = chartMode === "percentage" ? 0 : chartMode === "simulation" ? investmentAmount : 1;
    const totalTicks = 6;
    const step = (domainMax - domainMin) / (totalTicks - 1);
    const ticks = [anchor];
    for (let i = 1; anchor - i * step >= domainMin || anchor + i * step <= domainMax; i++) {
      const below = anchor - i * step;
      const above = anchor + i * step;
      if (below >= domainMin) ticks.unshift(below);
      if (above <= domainMax) ticks.push(above);
    }
    return ticks;
  })();

  function buildFormattedData(chartData) {
    let lastRenderedYear = null;
    let stepIndex = 0;

    return chartData.map((point) => {
      const fullDate = point.date;
      const currentYear = fullDate.slice(0, 4);
      const monthDay = fullDate.slice(5);

      let xAxisDate = "";
      const isNewYear = currentYear !== lastRenderedYear;
      const isLast30Days = period === "last30Days";
      const isLast7Days = period === "last7Days";
      const isLast365Days = period === "last365Days";
      
      const shouldShowStep = stepIndex % 2 === 0;

      if (isNewYear) {
        xAxisDate = fullDate;
        lastRenderedYear = currentYear;
        stepIndex = 0;
      } else {
        if ((isLast30Days && shouldShowStep) || isLast7Days) {
          xAxisDate = monthDay;
        }
        stepIndex++;
      }

      return {
        ...point,
        XaxisDate: xAxisDate,
      };
    });
  }

  const formattedData = buildFormattedData(mergedData);

  return (
    <ResponsiveContainer width="100%" height={600}>
      <LineChart
        data={formattedData}
        margin={{ top: 8, right: 8, bottom: 8, left: 16 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={currentTheme.palette.divider}
          vertical={false}
        />
        <XAxis
          dataKey="XaxisDate"
          interval="preserveStartEnd"
          tickLine={false}
        />
        <YAxis
          domain={domain}
          ticks={yAxisTicks}
          stroke={currentTheme.palette.text.secondary}
          tick={{
            fill: currentTheme.palette.text.secondary,
            fontSize: 12,
          }}
          tickLine={{ stroke: currentTheme.palette.text.secondary }}
          axisLine={{ stroke: currentTheme.palette.divider }}
          tickFormatter={modeConfig.formatAxisValue}
          width={modeConfig.yAxisWidth}
        />
        <Tooltip
          formatter={(value, name) => {
            const formattedValue = typeof value === "number" ? modeConfig.formatTooltipValue(value) : value;
            if (name === "value") return [formattedValue, isComparison ? (mainStrategy?.title ?? modeConfig.tooltipName) : modeConfig.tooltipName];
            const strategyId = name.replace("compare_", "");
            const compareStrategy = compareStrategiesData.find((s) => String(s.idStrategy) === strategyId);
            return [formattedValue, compareStrategy?.title ?? name];
          }}
          labelFormatter={(label, payload) =>
            `Date: ${payload?.[0]?.payload?.date}`
          }
          contentStyle={{
            backgroundColor: currentTheme.palette.background.default,
            border: `1px solid ${currentTheme.palette.divider}`,
            borderRadius: 8,
            color: currentTheme.palette.text.primary,
          }}
        />
        <Line
          type="monotoneX"
          dataKey="value"
          stroke={currentTheme.palette.primary.main}
          isAnimationActive={false}
          dot={false}
        />
        {compareStrategiesData.map((compareStrategy, index) => (
          <Line
            key={compareStrategy.idStrategy}
            type="monotoneX"
            dataKey={`compare_${compareStrategy.idStrategy}`}
            stroke={COMPARE_COLORS[index % COMPARE_COLORS.length]}
            isAnimationActive={false}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default Chart;
